import type { EnvironmentId, ProjectId, SourceControlProviderKind } from "@cmmd-forge/contracts";
import { detectGitRemoteIdentityFromRemoteUrl } from "@cmmd-forge/shared/git";

export interface PullRequestLinkTarget {
  readonly provider: SourceControlProviderKind;
  readonly host: string;
  readonly repository: string;
  readonly number: number;
}

export interface PullRequestLinkProject {
  readonly id: ProjectId;
  readonly environmentId: EnvironmentId;
  readonly repositoryIdentity?: {
    readonly locator: { readonly remoteUrl: string };
  } | null;
}

export interface ResolvedPullRequestLinkTarget extends PullRequestLinkTarget {
  readonly projectId: ProjectId;
  readonly environmentId: EnvironmentId;
}

export interface PullRequestProjectLink {
  readonly forgeEnvironmentId: string;
  readonly forgeProjectId: string;
  readonly cmmdProjectId: number;
}

export function resolveCmmdProjectId(
  links: ReadonlyArray<PullRequestProjectLink>,
  project: Pick<ResolvedPullRequestLinkTarget, "environmentId" | "projectId">,
): number | null {
  const matches = links.filter(
    (link) =>
      link.forgeEnvironmentId === project.environmentId &&
      link.forgeProjectId === project.projectId,
  );
  return matches.length === 1 ? matches[0]!.cmmdProjectId : null;
}

function positivePullRequestNumber(value: string | undefined): number | null {
  if (value === undefined || !/^\d+$/.test(value)) return null;
  const number = Number(value);
  return Number.isSafeInteger(number) && number > 0 ? number : null;
}

function decodedRepository(parts: ReadonlyArray<string>): string | null {
  try {
    const repository = parts
      .map((part) => decodeURIComponent(part))
      .join("/")
      .trim();
    return repository.length > 0 ? repository : null;
  } catch {
    return null;
  }
}

function githubTarget(url: URL): PullRequestLinkTarget | null {
  const match = /^\/([^/]+)\/([^/]+)\/pull\/(\d+)(?:\/.*)?$/i.exec(url.pathname);
  const number = positivePullRequestNumber(match?.[3]);
  const repository = decodedRepository([match?.[1] ?? "", match?.[2] ?? ""]);
  return number === null || repository === null
    ? null
    : { provider: "github", host: url.host.toLowerCase(), repository, number };
}

function gitlabTarget(url: URL): PullRequestLinkTarget | null {
  const marker = "/-/merge_requests/";
  const markerIndex = url.pathname.toLowerCase().indexOf(marker);
  if (markerIndex <= 0) return null;
  const repository = decodedRepository(url.pathname.slice(1, markerIndex).split("/"));
  const number = positivePullRequestNumber(
    url.pathname.slice(markerIndex + marker.length).split("/")[0],
  );
  return number === null || repository === null
    ? null
    : { provider: "gitlab", host: url.host.toLowerCase(), repository, number };
}

function bitbucketTarget(url: URL): PullRequestLinkTarget | null {
  const match = /^\/([^/]+)\/([^/]+)\/pull-requests\/(\d+)(?:\/.*)?$/i.exec(url.pathname);
  const number = positivePullRequestNumber(match?.[3]);
  const repository = decodedRepository([match?.[1] ?? "", match?.[2] ?? ""]);
  return number === null || repository === null
    ? null
    : { provider: "bitbucket", host: url.host.toLowerCase(), repository, number };
}

function azureTarget(url: URL): PullRequestLinkTarget | null {
  const segments = url.pathname.split("/").filter(Boolean);
  const gitIndex = segments.findIndex((segment) => segment.toLowerCase() === "_git");
  const pullRequestIndex = segments.findIndex(
    (segment, index) => index > gitIndex && segment.toLowerCase() === "pullrequest",
  );
  if (gitIndex < 1 || pullRequestIndex !== gitIndex + 2) return null;
  const number = positivePullRequestNumber(segments[pullRequestIndex + 1]);
  const repositoryName = segments[gitIndex + 1];
  const repository = decodedRepository([...segments.slice(0, gitIndex), repositoryName ?? ""]);
  return number === null || repository === null
    ? null
    : { provider: "azure-devops", host: url.host.toLowerCase(), repository, number };
}

/**
 * Parse a fully-qualified provider URL into the identity Forge needs to route internally.
 * Bare numbers are deliberately rejected: the workspace can contain the same number on many
 * repositories and environments, so guessing would open the wrong change request.
 */
export function parsePullRequestLink(input: string): PullRequestLinkTarget | null {
  let url: URL;
  try {
    url = new URL(input.trim());
  } catch {
    return null;
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") return null;
  const host = url.hostname.toLowerCase();
  if (host === "github.com" || host.endsWith(".githubenterprise.com")) return githubTarget(url);
  if (host === "gitlab.com" || host.includes("gitlab")) return gitlabTarget(url);
  if (host === "bitbucket.org" || host.includes("bitbucket")) return bitbucketTarget(url);
  if (host === "dev.azure.com" || host.endsWith(".visualstudio.com")) return azureTarget(url);
  return null;
}

export function shouldOpenPullRequestExternally(input: string): boolean {
  return parsePullRequestLink(input) === null;
}

/**
 * Resolve only against repositories actually present in Forge. Parsing a provider-looking URL is
 * intentionally insufficient: an unknown or ambiguous repository must remain an ordinary
 * external link instead of navigating to an inbox that cannot select it.
 */
export function resolvePullRequestLinkTarget(
  input: string,
  projects: ReadonlyArray<PullRequestLinkProject>,
): ResolvedPullRequestLinkTarget | null {
  const target = parsePullRequestLink(input);
  if (target === null) return null;
  const matches = projects.filter((project) => {
    const remoteUrl = project.repositoryIdentity?.locator.remoteUrl;
    if (remoteUrl === undefined) return false;
    const identity = detectGitRemoteIdentityFromRemoteUrl(remoteUrl);
    return (
      identity !== null &&
      identity.kind === target.provider &&
      identity.host === target.host &&
      identity.repositoryPath.toLowerCase() === target.repository.toLowerCase()
    );
  });
  if (matches.length !== 1) return null;
  return {
    ...target,
    projectId: matches[0]!.id,
    environmentId: matches[0]!.environmentId,
  };
}
